import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import BizMap from "../../components/smartreach/BizMap";
import {
  Loader2, Building2, Search, ShieldCheck, LogIn, CheckCircle2, Megaphone, Sparkles, Tag,
  Plus, X, DollarSign, Mail, Copy, Eye, MapPin, Star, Clock, UserCog, ArrowLeft, ArrowRight, AlertTriangle,
} from "lucide-react";
import { sr, actAs } from "../../lib/sr";
import { auth } from "../../lib/api";
import HealthBadge from "./HealthBadge";
import IndustryIntake, { missingRequired, PlaybookCard } from "../../components/smartreach/IndustryIntake";

const money = (n) => "$" + Number(n || 0).toLocaleString();

const Stat = ({ icon: Icon, label, value }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <Icon size={13} style={{ color: "var(--blue)" }} />
    <span className="sr-muted" style={{ fontSize: 12 }}>{value}</span>
    <span className="sr-muted" style={{ fontSize: 12, opacity: 0.7 }}>{label}</span>
  </div>
);

const SORTS = {
  newest: { label: "Newest", fn: (a, b) => (b.created_at || "").localeCompare(a.created_at || "") },
  name: { label: "Name A–Z", fn: (a, b) => (a.name || "").localeCompare(b.name || "") },
  live: { label: "Most live campaigns", fn: (a, b) => (b.live_campaigns || 0) - (a.live_campaigns || 0) },
  campaigns: { label: "Most campaigns", fn: (a, b) => (b.campaigns || 0) - (a.campaigns || 0) },
  budget: { label: "Highest budget", fn: (a, b) => (b.monthly_budget || 0) - (a.monthly_budget || 0) },
};

const WIZ_STEPS = ["How you sell", "Industry", "Business basics", "Details", "Owner & create"];

function DupWarning({ matches, onOpen }) {
  if (!matches || matches.length === 0) return null;
  return (
    <div className="sr-card" style={{ background: "#FFF7ED", border: "1px solid #FED7AA", marginBottom: 12 }} data-testid="dup-warning">
      <p style={{ fontSize: 13, color: "#9A3412", fontWeight: 700, display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
        <AlertTriangle size={14} /> Possible duplicate{matches.length > 1 ? "s" : ""}
      </p>
      <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
        {matches.map((m) => (
          <div key={m.tenant_id + m.reason} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12.5, color: "#7C2D12" }}>
              <b>{m.name}</b> already exists ({m.reason === "website" ? "same website" : "same name"}){m.archived ? " · archived" : ""}
            </span>
            {onOpen && !m.archived && (
              <button onClick={() => onOpen(m)} data-testid={`dup-open-${m.tenant_id}`} className="sr-btn sr-btn-sm" style={{ background: "#fff", padding: "4px 10px", fontSize: 11.5, whiteSpace: "nowrap" }}>
                <LogIn size={12} /> Open the existing client
              </button>
            )}
          </div>
        ))}
      </div>
      <p className="sr-muted" style={{ fontSize: 11.5, marginTop: 6, marginBottom: 0 }}>Opening the existing client avoids creating a second record — you can still create a new one if this is a different business.</p>
    </div>
  );
}

function WizHeader({ step }) {
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }} data-testid="wiz-header">
      {WIZ_STEPS.map((l, i) => {
        const n = i + 1; const on = n === step; const done = n < step;
        return (
          <div key={l} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700,
            color: on ? "var(--blue)" : done ? "var(--navy)" : "var(--muted)" }}>
            <span style={{ width: 20, height: 20, borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center",
              background: on ? "var(--blue)" : done ? "#D7F0DF" : "#eef1f6", color: on ? "#fff" : done ? "#067647" : "var(--muted)", fontSize: 11 }}>
              {done ? "✓" : n}
            </span>{l}
          </div>
        );
      })}
    </div>
  );
}

function WizFooter({ step, setStep, form, schema, busy }) {
  const next = () => {
    if (step === 2 && !form.industry) { toast.error("Pick an industry"); return; }
    if (step === 3 && !form.name.trim()) { toast.error("Enter a business name"); return; }
    if (step === 4) {
      const miss = missingRequired(schema, form.operating_model, form.industry, form.intake);
      if (miss.length) { toast.error(`Please complete: ${miss.join(", ")}`); return; }
    }
    setStep(Math.min(step + 1, 5));
  };
  return (
    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18 }} data-testid="wiz-footer">
      <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={step === 1 || busy} onClick={() => setStep(Math.max(step - 1, 1))} data-testid="wiz-back">
        <ArrowLeft size={15} /> Back
      </button>
      {step < 5 && (
        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={next} data-testid="wiz-next">
          Next <ArrowRight size={15} />
        </button>
      )}
    </div>
  );
}

export default function AdminClients() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [sort, setSort] = useState("newest");
  const [adding, setAdding] = useState(searchParams.get("new") === "1");
  const [wizStep, setWizStep] = useState(1);
  const [schema, setSchema] = useState(null);
  const [form, setForm] = useState({ name: "", website: "", category: "", owner_email: "", phone: "", address: "", hours: [], rating: null, rating_count: null, summary: "", reviews: [], maps_uri: "", send_invite: true, owner_type: "single_unit", operating_model: "physical", promote_types: ["business"], reporting_currency: "", sell_territories: "", conversion_location: "owned_site", industry: "", intake: {}, google_listed: null });
  const [busy, setBusy] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const aiPrefill = async () => {
    if (!form.name.trim() && !form.website.trim() && !form.category.trim()) {
      toast.error("Add a business name, website or category first"); return;
    }
    setPrefilling(true);
    try {
      const r = await sr.suggestIntake({ name: form.name, website: form.website, category: form.category, summary: form.summary, operating_model: form.operating_model });
      setForm((f) => ({ ...f, industry: r.industry || f.industry, intake: { ...(f.intake || {}), ...(r.intake || {}) } }));
      const lbl = (schema?.industries || []).find((i) => i.slug === r.industry)?.label || r.industry;
      toast.success(`AI suggests: ${lbl}. Review the details on the next step.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not auto-detect"); }
    finally { setPrefilling(false); }
  };
  const [enriching, setEnriching] = useState(false);
  const [created, setCreated] = useState(null);
  const [dupMatches, setDupMatches] = useState([]);
  const [emailDetecting, setEmailDetecting] = useState(false);
  const [emailSource, setEmailSource] = useState("");
  const [locate, setLocate] = useState({ q: "", city: "", state: "", country: "United States" });
  const [finding, setFinding] = useState(false);
  const [foundBiz, setFoundBiz] = useState(null);
  const [comps, setComps] = useState(null);
  const [compLoading, setCompLoading] = useState(false);
  const [selComps, setSelComps] = useState({});
  const [compKeyword, setCompKeyword] = useState("");
  const [aiKeywords, setAiKeywords] = useState([]);
  const [aiKwLoading, setAiKwLoading] = useState(false);
  const [pickedGeo, setPickedGeo] = useState({ lat: null, lng: null });
  const [staff, setStaff] = useState([]);
  const [assignCfg, setAssignCfg] = useState(null);
  const current = actAs.get();
  const me = auth.getUser();
  const isMine = (t) => (t.assigned_manager_id && t.assigned_manager_id === me?.id)
    || (!!t.assigned_manager_email && t.assigned_manager_email.toLowerCase() === (me?.email || "").toLowerCase());

  const findBusiness = async (queryOverride) => {
    const q = (typeof queryOverride === "string" ? queryOverride : locate.q).trim();
    if (!q) { toast.error("Enter a business name or keyword"); return; }
    setFinding(true); setFoundBiz(null);
    try {
      const r = await sr.adminBusinessSearch(q, { city: locate.city, state: locate.state, country: locate.country });
      if (!r.configured) toast.info("Business lookup pending activation — enter details manually below.");
      else if (!r.results?.length) toast.info("No local matches — try a different name or area.");
      setFoundBiz(r.results || []);
    } catch { toast.error("Search failed"); } finally { setFinding(false); }
  };
  const pickBusiness = async (b) => {
    if (b.existing_client) { toast.error(`${b.name} is already a client — skipping to avoid a duplicate.`); return; }
    setForm((f) => ({ ...f, name: b.name || f.name, website: b.website || f.website, category: b.category || f.category, phone: b.phone || f.phone, address: b.address || f.address, place_id: b.place_id,
      rating: b.rating ?? f.rating, rating_count: b.rating_count ?? f.rating_count, hours: (b.hours && b.hours.length) ? b.hours : f.hours, summary: b.summary || f.summary, maps_uri: b.maps_uri || f.maps_uri }));
    setFoundBiz(null);
    setPickedGeo({ lat: b.lat ?? null, lng: b.lng ?? null });
    toast.success(`Selected ${b.name}`);
    loadCompetitors(b);
    loadAiKeywords(b.name, b.category);
    if (b.place_id) {
      setEnriching(true);
      try {
        const d = await sr.adminBusinessDetails(b.place_id);
        const p = d.place;
        if (p) setForm((f) => ({ ...f, website: f.website || p.website || "", phone: f.phone || p.phone || "", address: f.address || p.address || "", category: f.category || p.category || "",
          rating: p.rating ?? f.rating, rating_count: p.rating_count ?? f.rating_count, hours: (p.hours && p.hours.length) ? p.hours : f.hours, summary: p.summary || f.summary, reviews: (p.reviews && p.reviews.length) ? p.reviews : f.reviews, maps_uri: p.maps_uri || f.maps_uri }));
      } catch { /* keep search-level data */ } finally { setEnriching(false); }
    }
  };
  const loadCompetitors = async (b) => {
    const kw = b.category || locate.q || "";
    setCompKeyword(kw);
    setComps(null); setSelComps({}); setCompLoading(true);
    try {
      const r = await sr.adminCompetitorSearch({ category: kw, city: locate.city, state: locate.state, country: locate.country, lat: b.lat ?? null, lng: b.lng ?? null, exclude_place_id: b.place_id, exclude_name: b.name });
      setComps(r.competitors || []);
    } catch { setComps([]); } finally { setCompLoading(false); }
  };
  const loadAiKeywords = async (name, category) => {
    setAiKeywords([]); setAiKwLoading(true);
    try { const r = await sr.competitorKeywords(name, category); setAiKeywords(r.keywords || []); }
    catch { setAiKeywords([]); } finally { setAiKwLoading(false); }
  };
  const searchCompetitors = async (kwOverride) => {
    const kw = (typeof kwOverride === "string" ? kwOverride : compKeyword || "").trim();
    if (!kw) { toast.error("Type what kind of competitor to find (e.g. Tax preparation)"); return; }
    if (typeof kwOverride === "string") setCompKeyword(kw);
    setComps(null); setCompLoading(true);
    try {
      const r = await sr.adminCompetitorSearch({ category: kw, city: locate.city, state: locate.state, country: locate.country, lat: pickedGeo.lat, lng: pickedGeo.lng, exclude_place_id: form.place_id, exclude_name: form.name });
      setComps(r.competitors || []);
      if (!(r.competitors || []).length) toast.info("No matches — try a different keyword or widen the area.");
    } catch { toast.error("Competitor search failed"); setComps([]); } finally { setCompLoading(false); }
  };
  const toggleComp = (c) => setSelComps((s) => ({ ...s, [c.place_id]: s[c.place_id] ? undefined : c }));
  const allCompsSelected = comps && comps.length > 0 && comps.every((c) => selComps[c.place_id]);
  const toggleAllComps = () => {
    if (allCompsSelected) { setSelComps({}); }
    else { const m = {}; (comps || []).forEach((c) => { m[c.place_id] = c; }); setSelComps(m); }
  };
  const NEARBY_CATS = ["Restaurants", "Dentists", "Med spas", "Auto repair", "Salons", "Gyms", "Law firms", "Real estate", "Accountants", "Tax prep", "Insurance"];
  const VERTICAL_PRESETS = [
    { label: "Tax prep", kw: "Tax preparation" }, { label: "Accounting", kw: "Accountant" },
    { label: "Dental", kw: "Dentist" }, { label: "HVAC", kw: "HVAC contractor" },
    { label: "Restaurants", kw: "Restaurant" }, { label: "Salons", kw: "Hair salon" },
    { label: "Auto repair", kw: "Auto repair" }, { label: "Law firms", kw: "Law firm" },
    { label: "Real estate", kw: "Real estate agency" }, { label: "Med spa", kw: "Med spa" },
    { label: "Gym", kw: "Gym" }, { label: "Insurance", kw: "Insurance agency" },
  ];
  const browseCategory = (cat) => { setLocate((l) => ({ ...l, q: cat })); findBusiness(cat); };

  const load = () => sr.adminTenants(showArchived).then(setData).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, [showArchived]);
  useEffect(() => {
    sr.adminStaff().then((r) => setStaff(r.staff || [])).catch(() => {});
    sr.adminAssignSettings().then(setAssignCfg).catch(() => {});
    sr.onboardingSchema().then(setSchema).catch(() => {});
  }, []);

  function suggestOwnerEmail(f) {
    const site = (f.website || "").trim().toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").split(/[/?#]/)[0];
    if (site && site.includes(".")) return `owner@${site}`;
    const slug = (f.name || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
    return slug ? `owner@${slug}.com` : "";
  }
  // Prefill a sensible owner email on the owner step so new clients get a login by default.
  useEffect(() => {
    if (adding && wizStep === 5 && !form.owner_email) {
      const s = suggestOwnerEmail(form);
      if (s) { setForm((f) => (f.owner_email ? f : { ...f, owner_email: s })); setEmailSource("suggested"); }
    }
  }, [wizStep, adding]);
  // Smart email detect: pull a real owner/contact email from the pasted website.
  useEffect(() => {
    if (!adding) return;
    const website = (form.website || "").trim();
    if (!website || !website.includes(".") || form.owner_email) return;
    const h = setTimeout(async () => {
      setEmailDetecting(true);
      try {
        const r = await sr.adminDetectEmail(website);
        if (r && r.email) { setForm((f) => (f.owner_email ? f : { ...f, owner_email: r.email })); setEmailSource("detected"); }
      } catch { /* best-effort */ }
      finally { setEmailDetecting(false); }
    }, 900);
    return () => clearTimeout(h);
  }, [form.website, adding]);
  // Warn when a new client's name or website matches an existing client.
  useEffect(() => {
    if (!adding) { setDupMatches([]); return; }
    const name = (form.name || "").trim();
    const website = (form.website || "").trim();
    if (name.length < 2 && !website) { setDupMatches([]); return; }
    const h = setTimeout(() => {
      sr.adminCheckDuplicate(name, website).then((r) => setDupMatches(r.matches || [])).catch(() => {});
    }, 500);
    return () => clearTimeout(h);
  }, [form.name, form.website, adding]);

  const quickAssign = async (t, managerId) => {
    try { await sr.adminAssignManager(t.id, managerId); toast.success(managerId ? "Manager assigned" : "Manager cleared"); load(); sr.adminAssignSettings().then(setAssignCfg).catch(() => {}); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't assign manager"); }
  };
  const toggleAutoAssign = async () => {
    const next = !assignCfg?.enabled;
    try { const r = await sr.adminSetAssignSettings(next); setAssignCfg((c) => ({ ...c, enabled: r.enabled })); toast.success(next ? "New clients will be auto-assigned round-robin" : "Auto-assign turned off"); }
    catch { toast.error("Couldn't update auto-assign"); }
  };
  const runAutoAssign = async () => {
    try { const r = await sr.adminAutoAssign(); toast.success(`Assigned ${r.assigned} client(s) round-robin`); load(); sr.adminAssignSettings().then(setAssignCfg).catch(() => {}); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't auto-assign"); }
  };

  const manage = async (t) => {
    actAs.set(t.id, t.name);
    try { await sr.adminActAs(t.id); } catch { /* logging is best-effort */ }
    toast.success(`Now managing ${t.name}`);
    window.location.assign("/app");
  };

  const createClient = async () => {
    if (!form.name.trim()) { toast.error("Enter a client business name"); return; }
    if (form.owner_email && !form.owner_email.includes("@")) { toast.error("Enter a valid owner email"); return; }
    setBusy(true);
    try {
      const comps = Object.values(selComps).filter(Boolean).map((c) => ({ name: c.name, place_id: c.place_id, address: c.address }));
      const intake = form.intake || {};
      const r = await sr.adminCreateClient({ ...form, competitors: comps,
        industry: form.industry || undefined, intake,
        store_url: intake.store_url || undefined, ecommerce_platform: intake.ecommerce_platform || undefined,
        avg_order_value: (intake.avg_order_value === "" || intake.avg_order_value == null) ? undefined : Number(intake.avg_order_value),
        google_listed: form.google_listed,
        sell_territories: form.sell_territories ? form.sell_territories.split(",").map((s) => s.trim()).filter(Boolean) : undefined });
      setCreated(r);
      toast.success(`Created ${r.name}${comps.length ? ` with ${comps.length} competitor target(s)` : ""}`);
      setComps(null); setSelComps({});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create client"); }
    finally { setBusy(false); }
  };

  const startAnother = () => {
    setCreated(null);
    setForm({ name: "", website: "", category: "", owner_email: "", phone: "", address: "", place_id: "", hours: [], rating: null, rating_count: null, summary: "", reviews: [], maps_uri: "", send_invite: true, owner_type: "single_unit", operating_model: "physical", promote_types: ["business"], reporting_currency: "", sell_territories: "", conversion_location: "owned_site", industry: "", intake: {}, google_listed: null });
    setWizStep(1);
    setComps(null); setSelComps({});
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">Could not load clients. Admin access is required.</p></div>;

  const passFilter = (t) => {
    if (filter === "mine") return isMine(t);
    if (filter === "live") return (t.live_campaigns || 0) > 0;
    if (filter === "demo") return !!t.is_demo;
    if (filter === "real") return !t.is_demo;
    if (filter === "attention") return ["red", "amber"].includes(t.health?.tone);
    return true;
  };
  const tenants = (data.tenants || []).filter((t) => {
    const s = q.trim().toLowerCase();
    const match = !s || (t.name || "").toLowerCase().includes(s) ||
      (t.owner_email || "").toLowerCase().includes(s) || (t.owner_name || "").toLowerCase().includes(s);
    return match && passFilter(t);
  }).sort(SORTS[sort].fn);

  const FILTERS = [
    ["all", "All", data.tenants.length],
    ["mine", "Assigned to me", data.tenants.filter(isMine).length],
    ["live", "Live", data.tenants.filter((t) => (t.live_campaigns || 0) > 0).length],
    ["attention", "Needs attention", data.tenants.filter((t) => ["red", "amber"].includes(t.health?.tone)).length],
    ["demo", "Demo", data.tenants.filter((t) => t.is_demo).length],
    ["real", "Real", data.tenants.filter((t) => !t.is_demo).length],
  ];

  return (
    <div data-testid="admin-clients-page">
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span className="sr-badge" style={{ marginBottom: 8 }}><ShieldCheck size={12} /> Admin · Clients</span>
          <h1 style={{ fontSize: 30 }}>All client businesses</h1>
          <p className="sr-muted" style={{ marginTop: 6 }}>Every SMB tenant on SmartReachAI. Pick a client to manage their workspace and create campaigns on their behalf.</p>
        </div>
        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => { setAdding(!adding); setCreated(null); setWizStep(1); }} data-testid="admin-add-client-btn">
          {adding ? <><X size={15} /> Cancel</> : <><Plus size={15} /> New client</>}
        </button>
      </div>

      {/* Auto-assign controls */}
      {assignCfg && (
        <div className="sr-card" style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }} data-testid="auto-assign-bar">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <UserCog size={16} style={{ color: "var(--blue)" }} />
            <div>
              <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 14 }}>Manager assignment</div>
              <div className="sr-muted" style={{ fontSize: 12 }}>{assignCfg.unassigned} client(s) currently unassigned</div>
            </div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto", cursor: "pointer", fontSize: 13 }} data-testid="auto-assign-toggle-label">
            <input type="checkbox" checked={!!assignCfg.enabled} onChange={toggleAutoAssign} data-testid="auto-assign-toggle" />
            Auto-assign new clients (round-robin)
          </label>
          <button className="sr-btn sr-btn-sm" onClick={runAutoAssign} disabled={!assignCfg.unassigned} data-testid="auto-assign-run">
            <Sparkles size={14} /> Round-robin unassigned{assignCfg.unassigned ? ` (${assignCfg.unassigned})` : ""}
          </button>
        </div>
      )}

      {adding && (
        <div className="sr-card" style={{ marginBottom: 16 }} data-testid="admin-add-client-form">
          <div className="sr-eyebrow" style={{ marginBottom: 12 }}>Onboard a new client</div>
          {!created && <WizHeader step={wizStep} />}

          <div style={{ display: created ? "none" : "block" }} data-testid="wiz-steps">
          <div style={{ display: wizStep === 1 ? "block" : "none" }} data-testid="wiz-step-1">
            <IndustryIntake schema={schema} sections={["sellType"]} idPrefix="newcli"
              sellType={form.operating_model} onSellType={(v) => setForm({ ...form, operating_model: v })} />
          </div>

          <div style={{ display: wizStep === 2 ? "block" : "none" }} data-testid="wiz-step-2">
            <IndustryIntake schema={schema} sections={["industry"]} idPrefix="newcli"
              industry={form.industry} onIndustry={(v) => setForm({ ...form, industry: v })} />
          </div>

          <div style={{ display: wizStep === 3 ? "block" : "none" }} data-testid="wiz-step-3">
          <DupWarning matches={dupMatches} onOpen={(m) => manage({ id: m.tenant_id, name: m.name })} />
          <IndustryIntake schema={schema} sections={["google"]} idPrefix="newcli" sellType={form.operating_model}
            googleListed={form.google_listed} onGoogleListed={(v) => setForm({ ...form, google_listed: v })} />
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={aiPrefill} disabled={prefilling} data-testid="newcli-ai-prefill">
              {prefilling ? <Loader2 size={14} className="sr-spin" /> : <Sparkles size={14} />} Auto-detect industry &amp; details with AI
            </button>
          </div>
          {form.operating_model !== "online" && form.google_listed !== false && (
          <div style={{ background: "var(--bg-subtle, #f7f9fc)", borderRadius: 8, padding: 12, marginBottom: 14, marginTop: 12 }} data-testid="new-client-locator">
            <label className="sr-label" style={{ marginBottom: 6 }}>Find a local business (search Google in a specific area)</label>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr auto", gap: 8 }}>
              <input className="sr-input" placeholder="Business name or keyword" value={locate.q} onChange={(e) => setLocate({ ...locate, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && findBusiness()} data-testid="locate-query" />
              <input className="sr-input" placeholder="City" value={locate.city} onChange={(e) => setLocate({ ...locate, city: e.target.value })} data-testid="locate-city" />
              <input className="sr-input" placeholder="State/Region" value={locate.state} onChange={(e) => setLocate({ ...locate, state: e.target.value })} data-testid="locate-state" />
              <input className="sr-input" placeholder="Country" value={locate.country} onChange={(e) => setLocate({ ...locate, country: e.target.value })} data-testid="locate-country" />
              <button className="sr-btn sr-btn-primary" onClick={() => findBusiness()} disabled={finding} data-testid="locate-search">{finding ? <Loader2 size={15} className="sr-spin" /> : <><Search size={15} /> Find</>}</button>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }} data-testid="nearby-cats">
              <span className="sr-muted" style={{ fontSize: 12, alignSelf: "center" }}>Browse a category:</span>
              {NEARBY_CATS.map((c) => (
                <button key={c} className="sr-btn sr-btn-sm" onClick={() => browseCategory(c)} data-testid={`nearby-cat-${c.replace(/\s+/g, "-").toLowerCase()}`} style={{ fontSize: 12 }}>{c}</button>
              ))}
            </div>
            {foundBiz && foundBiz.length > 0 && (
              <>
                <BizMap points={foundBiz} selectedId={form.place_id} onSelect={pickBusiness} />
                <div style={{ marginTop: 10, display: "grid", gap: 6, maxHeight: 240, overflowY: "auto" }} data-testid="locate-results">
                  {foundBiz.map((b) => (
                    <button key={b.place_id} onClick={() => pickBusiness(b)} data-testid={`locate-result-${b.place_id}`} disabled={b.existing_client}
                      style={{ textAlign: "left", cursor: b.existing_client ? "not-allowed" : "pointer", opacity: b.existing_client ? 0.6 : 1, padding: 10, border: "1px solid var(--border)", borderRadius: 6, background: "#fff", display: "flex", gap: 8, alignItems: "flex-start" }}>
                      <MapPin size={15} color="var(--blue)" style={{ marginTop: 2 }} />
                      <div style={{ flex: 1 }}><strong style={{ color: "var(--navy)" }}>{b.name}</strong>
                        {b.existing_client && <span className="sr-badge sr-badge-amber" style={{ marginLeft: 6, fontSize: 11 }} data-testid={`dupe-${b.place_id}`}>Already a client</span>}
                        <div className="sr-muted" style={{ fontSize: 12 }}>{b.address}{b.category ? ` · ${b.category}` : ""}</div></div>
                    </button>
                  ))}
                </div>
              </>
            )}
            {foundBiz && foundBiz.length === 0 && <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>No matches — refine the name/area or fill in the details below.</p>}
          </div>
          )}
          {(compLoading || comps) && (
            <div style={{ background: "var(--bg-subtle, #f7f9fc)", borderRadius: 8, padding: 12, marginBottom: 14 }} data-testid="competitor-panel">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <label className="sr-label" style={{ margin: 0 }}>Competitor targeting {form.name ? `for ${form.name}` : ""}</label>
                {comps && comps.length > 0 && (
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }} data-testid="comp-select-all-label">
                    <input type="checkbox" checked={allCompsSelected} onChange={toggleAllComps} data-testid="comp-select-all" /> Select all
                  </label>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }} data-testid="comp-custom-search">
                <input className="sr-input" style={{ flex: 1 }} value={compKeyword} onChange={(e) => setCompKeyword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchCompetitors()} placeholder="Find competitors by type or name — e.g. Tax preparation, Accountant"
                  data-testid="comp-keyword-input" />
                <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={searchCompetitors} disabled={compLoading} data-testid="comp-search-btn">
                  {compLoading ? <Loader2 size={14} className="sr-spin" /> : <><Search size={14} /> Find</>}
                </button>
              </div>
              <p className="sr-muted" style={{ fontSize: 11, marginTop: -4, marginBottom: 8 }}>Not the right fit? Edit the keyword above and search again to pull competitors that actually match.</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }} data-testid="comp-ai-keywords">
                <span className="sr-muted" style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}><Sparkles size={12} /> {aiKwLoading ? "Suggesting…" : "AI suggestions:"}</span>
                {aiKwLoading && <Loader2 size={12} className="sr-spin" />}
                {aiKeywords.map((k) => (
                  <button key={`ai-${k}`} className="sr-btn sr-btn-sm" onClick={() => searchCompetitors(k)} data-testid={`comp-ai-kw-${k.replace(/\s+/g, "-").toLowerCase()}`} style={{ fontSize: 12 }}>{k}</button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }} data-testid="comp-presets">
                <span className="sr-muted" style={{ fontSize: 11 }}>Quick presets:</span>
                {VERTICAL_PRESETS.map((p) => (
                  <button key={`preset-${p.kw}`} className="sr-btn sr-btn-sm" onClick={() => searchCompetitors(p.kw)} data-testid={`comp-preset-${p.label.replace(/\s+/g, "-").toLowerCase()}`} style={{ fontSize: 12 }}>{p.label}</button>
                ))}
              </div>
              {compLoading ? <Loader2 size={16} className="sr-spin" />
                : comps.length === 0 ? <p className="sr-muted" style={{ fontSize: 12 }}>No nearby competitors found for this category/area.</p>
                  : (
                    <>
                      <p className="sr-muted" style={{ fontSize: 12, marginBottom: 6 }}>Select nearby {form.category || "same-category"} businesses to target as competitors:</p>
                      <div style={{ display: "grid", gap: 4, maxHeight: 200, overflowY: "auto" }}>
                        {comps.map((c) => (
                          <label key={c.place_id} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 8px", background: "#fff", border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer", fontSize: 13 }} data-testid={`comp-${c.place_id}`}>
                            <input type="checkbox" checked={!!selComps[c.place_id]} onChange={() => toggleComp(c)} data-testid={`comp-check-${c.place_id}`} style={{ marginTop: 3 }} />
                            <span><strong>{c.name}</strong><span className="sr-muted" style={{ display: "block", fontSize: 12 }}>{c.address}</span></span>
                          </label>
                        ))}
                      </div>
                      <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>{Object.values(selComps).filter(Boolean).length} selected — saved with the client.</p>
                    </>
                  )}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="sr-field"><label className="sr-label">Business name *</label>
              <input className="sr-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-client-name" /></div>
            <div className="sr-field"><label className="sr-label">Website (optional)</label>
              <input className="sr-input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="new-client-website" /></div>
            <div className="sr-field"><label className="sr-label">Category (optional)</label>
              <input className="sr-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="new-client-category" /></div>
            <div className="sr-field"><label className="sr-label">Phone (optional)</label>
              <input className="sr-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="new-client-phone" /></div>
            <div className="sr-field" style={{ gridColumn: "1 / -1" }}><label className="sr-label">Address (optional)</label>
              <input className="sr-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="new-client-address" /></div>
            <div className="sr-field" style={{ gridColumn: "1 / -1" }} data-testid="new-client-owner-type">
              <label className="sr-label">Business structure</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[
                  { key: "single_unit", label: "Single-unit owner" },
                  { key: "multi_unit", label: "Multi-unit owner" },
                  { key: "franchisor", label: "Franchisor" },
                ].map((o) => {
                  const on = form.owner_type === o.key;
                  return (
                    <button key={o.key} type="button" onClick={() => setForm({ ...form, owner_type: o.key })} data-testid={`new-client-owner-type-${o.key}`}
                      style={{ border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "var(--blue)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 999, padding: "7px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          {(enriching || form.rating || (form.hours && form.hours.length) || (form.reviews && form.reviews.length) || form.summary) && (
            <div className="sr-card" style={{ marginTop: 12, background: "#F7FAFF", border: "1px solid #DCE6F5" }} data-testid="imported-details">
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <MapPin size={14} style={{ color: "var(--blue)" }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)" }}>Imported from Google — confirm &amp; edit above</span>
                {enriching && <Loader2 size={13} className="sr-spin" />}
              </div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", fontSize: 13, color: "var(--navy)" }}>
                {form.rating != null && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }} data-testid="imported-rating">
                    <Star size={14} style={{ color: "#E0A94A", fill: "#E0A94A" }} /> <b>{form.rating}</b>
                    {form.rating_count ? <span className="sr-muted">({form.rating_count} reviews)</span> : null}
                  </span>
                )}
                {form.hours && form.hours.length > 0 && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }} data-testid="imported-hours">
                    <Clock size={14} style={{ color: "var(--blue)" }} /> Hours imported ({form.hours.length} days)
                  </span>
                )}
                {form.maps_uri && <a href={form.maps_uri} target="_blank" rel="noreferrer" style={{ fontSize: 12 }} data-testid="imported-maps-link">View on Google Maps →</a>}
              </div>
              {form.summary && <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }} data-testid="imported-summary">{form.summary}</p>}
              {form.reviews && form.reviews.length > 0 && (
                <div style={{ marginTop: 8 }} data-testid="imported-reviews">
                  <div className="sr-muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Review highlights (used as AI context)</div>
                  <div style={{ display: "grid", gap: 6 }}>
                    {form.reviews.slice(0, 3).map((rv, i) => (
                      <div key={i} style={{ fontSize: 12, color: "var(--navy)", background: "#fff", border: "1px solid var(--border)", borderRadius: 6, padding: "6px 8px" }} data-testid={`imported-review-${i}`}>
                        <b>{rv.rating ? `${rv.rating}★` : ""}</b> {String(rv.text || "").slice(0, 160)}{String(rv.text || "").length > 160 ? "…" : ""}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          </div>

          <div style={{ display: wizStep === 4 ? "block" : "none" }} data-testid="wiz-step-4">
            <div className="sr-field" data-testid="new-client-promote-types">
              <label className="sr-label">What does this business promote? <span className="sr-muted" style={{ fontWeight: 400 }}>(pick all that apply)</span></label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[{ key: "products", label: "Products" }, { key: "services", label: "Services / subscriptions" }, { key: "movies", label: "Movies & releases" }, { key: "events", label: "Events" }, { key: "business", label: "The whole business" }].map((o) => {
                  const on = (form.promote_types || []).includes(o.key);
                  return (
                    <button key={o.key} type="button" data-testid={`new-client-promote-${o.key}`}
                      onClick={() => setForm({ ...form, promote_types: on ? form.promote_types.filter((x) => x !== o.key) : [...(form.promote_types || []), o.key] })}
                      style={{ border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "var(--blue)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 999, padding: "7px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                      {on ? "✓ " : ""}{o.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <IndustryIntake schema={schema} sections={["questions"]} idPrefix="newcli"
                sellType={form.operating_model} industry={form.industry}
                intake={form.intake} onIntake={(v) => setForm({ ...form, intake: v })} />
            </div>
          </div>

          <div style={{ display: wizStep === 5 ? "block" : "none" }} data-testid="wiz-step-5">
            {form.industry && schema?.playbooks?.[form.industry] && (
              <div style={{ marginBottom: 14 }}>
                <PlaybookCard playbook={schema.playbooks[form.industry]} />
              </div>
            )}
            <div className="sr-field"><label className="sr-label">Owner email</label>
              <input className="sr-input" value={form.owner_email} onChange={(e) => { setForm({ ...form, owner_email: e.target.value }); setEmailSource("manual"); }} data-testid="new-client-email" placeholder="owner@business.com" />
              {emailDetecting ? (
                <p className="sr-muted" style={{ fontSize: 11.5, marginTop: 5, display: "flex", alignItems: "center", gap: 6 }}><Loader2 size={11} className="sr-spin" /> Looking for the owner's email on the website…</p>
              ) : emailSource === "detected" ? (
                <p style={{ fontSize: 11.5, marginTop: 5, color: "#067647", display: "flex", alignItems: "center", gap: 6 }} data-testid="new-client-email-detected"><CheckCircle2 size={12} /> Pulled from the website — edit if needed, or clear to skip creating a login.</p>
              ) : (
                <p className="sr-muted" style={{ fontSize: 11.5, marginTop: 5 }}>Prefilled so they get a login and you can jump straight to their workspace — edit to the owner's real email, or clear it to skip creating a login.</p>
              )}</div>
            {form.owner_email && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, cursor: "pointer", fontSize: 13, color: "var(--navy)" }} data-testid="new-client-invite-toggle">
                <input type="checkbox" checked={form.send_invite} onChange={(e) => setForm({ ...form, send_invite: e.target.checked })} />
                <Mail size={14} /> Email the owner an invite with sign-in details
              </label>
            )}
            <DupWarning matches={dupMatches} onOpen={(m) => manage({ id: m.tenant_id, name: m.name })} />
            <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 12 }} onClick={createClient} disabled={busy} data-testid="new-client-submit">
              {busy ? <Loader2 size={15} className="sr-spin" /> : <><Plus size={15} /> Create client</>}
            </button>
          </div>

          <WizFooter step={wizStep} setStep={setWizStep} form={form} schema={schema} busy={busy} />
          </div>

          {created && (
            <div className="sr-card" style={{ background: "#F1FBF4", border: "1px solid #B6E3C6" }} data-testid="new-client-result">
              <p style={{ fontSize: 15, color: "#067647", fontWeight: 800 }}><CheckCircle2 size={16} /> {created.name} created</p>
              {created.owner ? (
                <>
                  <p className="sr-muted" style={{ fontSize: 13, marginTop: 6 }}>Owner: <b>{created.owner.email}</b>{created.owner.existing ? " (existing account attached)" : ""}</p>
                  {created.owner.temp_password && (
                    <p className="sr-muted" style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                      Temp password: <b>{created.owner.temp_password}</b>
                      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => copy(created.owner.temp_password)} data-testid="copy-temp-pw"><Copy size={12} /></button>
                    </p>
                  )}
                  <p className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>{created.owner.emailed ? "Invite emailed ✓" : "Share the sign-in details securely."} Sign-in: {created.owner.login_url}</p>
                </>
              ) : <p className="sr-muted" style={{ fontSize: 13, marginTop: 6 }}>No owner login created — open the workspace to set it up yourself.</p>}
              <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => manage({ id: created.tenant_id, name: created.name })} data-testid="new-client-open-workspace">
                  <ArrowRight size={14} /> Open {created.name} workspace
                </button>
                <button className="sr-btn sr-btn-sm" onClick={startAnother} data-testid="new-client-add-another">
                  <Plus size={14} /> Add another client
                </button>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => { setAdding(false); setCreated(null); }} data-testid="new-client-done">
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="sr-card" style={{ marginBottom: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <Search size={16} style={{ color: "var(--muted)" }} />
        <input className="sr-input" placeholder="Search by business, owner name or email" value={q}
          onChange={(e) => setQ(e.target.value)} data-testid="admin-clients-search" style={{ border: "none", flex: 1, minWidth: 180 }} />
        <select className="sr-select" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="admin-clients-sort" style={{ maxWidth: 220 }}>
          {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{`Sort: ${v.label}`}</option>)}
        </select>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }} data-testid="admin-clients-filters">
        {FILTERS.map(([key, label, count]) => {
          const active = filter === key;
          return (
            <button key={key} onClick={() => setFilter(key)} data-testid={`client-filter-${key}`}
              style={{ border: "1px solid " + (active ? "var(--blue)" : "var(--border)"), borderRadius: 999,
                background: active ? "var(--blue)" : "#fff", color: "var(--navy)",
                fontSize: 12, fontWeight: 700, padding: "6px 12px", cursor: "pointer" }}>
              {label} <span style={{ opacity: 0.75 }}>· {count}</span>
            </button>
          );
        })}
        <button onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived"
          style={{ border: "1px solid " + (showArchived ? "var(--navy)" : "var(--border)"), borderRadius: 999,
            background: showArchived ? "var(--navy)" : "#fff", color: showArchived ? "#fff" : "var(--navy)",
            fontSize: 12, fontWeight: 700, padding: "6px 12px", cursor: "pointer", marginLeft: "auto" }}>
          {showArchived ? "Showing archived" : "Show archived"}
        </button>
      </div>

      {tenants.length === 0 ? (
        <div className="sr-card" data-testid="admin-clients-empty"><p className="sr-muted">No clients match your search or filter.</p></div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {tenants.map((t) => {
            const isCurrent = current?.tenant_id === t.id;
            return (
              <div key={t.id} className="sr-card" data-testid={`admin-client-${t.id}`}
                style={isCurrent ? { border: "2px solid var(--blue)" } : {}}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <Building2 size={16} style={{ color: "var(--navy)" }} />
                      <strong style={{ color: "var(--navy)", fontSize: 17 }}>{t.name}</strong>
                      {t.is_demo && <span className="sr-badge sr-badge-amber">Demo</span>}
                      {t.archived && <span className="sr-badge sr-badge-gray" data-testid={`archived-badge-${t.id}`}>Archived</span>}
                      {isCurrent && <span className="sr-badge sr-badge-green"><CheckCircle2 size={11} /> Managing now</span>}
                      <HealthBadge health={t.health} size="sm" />
                    </div>
                    <div className="sr-muted" style={{ fontSize: 13, marginTop: 4 }}>
                      {t.owner_name ? `${t.owner_name} · ` : ""}{t.owner_email || "no owner email"}
                      {t.owner_role ? ` · ${t.owner_role.replace(/_/g, " ")}` : ""}
                    </div>
                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 10 }}>
                      <Stat icon={Megaphone} label="campaigns" value={t.campaigns} />
                      <Stat icon={CheckCircle2} label="live" value={t.live_campaigns} />
                      <Stat icon={DollarSign} label="/mo budget" value={money(t.monthly_budget)} />
                      <Stat icon={Sparkles} label="growth plans" value={t.growth_plans} />
                      <Stat icon={Tag} label="offers" value={t.offers} />
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <select className="sr-select" value={t.assigned_manager_id || ""} data-testid={`assign-manager-${t.id}`}
                      onChange={(e) => quickAssign(t, e.target.value || null)} style={{ maxWidth: 200, fontSize: 12 }}>
                      <option value="">Unassigned</option>
                      {staff.map((s) => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
                    </select>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav(`/app/admin/clients/${t.id}`)} data-testid={`admin-view-${t.id}`}>
                      <Eye size={14} /> View details
                    </button>
                    <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => manage(t)} data-testid={`admin-manage-${t.id}`}>
                      <LogIn size={14} /> {isCurrent ? "Open workspace" : "Manage / Act as"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
